import * as React from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { ViewContext } from "components/lib";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { cn } from "../../../helpers/cn";
import { useQuoteUi } from "./QuoteUiContext";
import { FullSchema } from "../utils/schema";

function formatCarrierList(list = []) {
  if (!list.length) return "";
  if (list.length === 1) return list[0];
  if (list.length === 2) return list.join(" and ");
  return `${list.slice(0, -1).join(", ")}, and ${list.slice(-1)}`;
}

function sanitize(obj) {
  return JSON.parse(
    JSON.stringify(obj, (_k, v) => {
      if (v instanceof Date) return v.toISOString();
      return v;
    })
  );
}

export function MultiStepFormLayout({
  user,
  producers,
  methods,
  schema,
  schemaBase,
  stepComponents,
}) {
  const {
    state: { currentStep, singlePage, maxStepReached },
    dispatch,
    steps,
  } = useQuoteUi();
  const location = useLocation();
  const navigate = useNavigate();
  const context = React.useContext(ViewContext);
  const { handleSubmit } = useFormContext();

  const [submittingState, setSubmittingState] = React.useState(false);
  const [savingState, setSavingState] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [bannerText, setBannerText] = React.useState("");

  const activeSchema = schema ?? FullSchema;
  const activeSchemaBase = schemaBase ?? activeSchema;
  const SCHEMA_KEYS = React.useMemo(
    () => Object.keys(activeSchemaBase.shape),
    [activeSchemaBase]
  );

  const STEP_FIELDS = React.useMemo(() => {
    const map = {};
    for (let i = 1; i <= steps.length; i += 1) {
      const upto = Math.min(i, SCHEMA_KEYS.length);
      map[i] = SCHEMA_KEYS.slice(0, upto);
    }
    return map;
  }, [SCHEMA_KEYS, steps.length]);

  const values = methods.watch();
  const canSubmit = activeSchema.safeParse(values ?? {}).success;

  const stepValidity = React.useMemo(() => {
    const map = {};
    steps.forEach((s) => {
      const fields = STEP_FIELDS[s.id] ?? [];
      if (!fields.length) {
        map[s.id] = true;
        return;
      }
      const slice = Object.fromEntries(
        Object.entries(values ?? {}).filter(([k]) => fields.includes(k))
      );
      const schemaSubset = activeSchemaBase.pick(
        Object.fromEntries(fields.map((f) => [f, true]))
      );
      map[s.id] = schemaSubset.safeParse(slice).success;
    });
    return map;
  }, [steps, STEP_FIELDS, values, activeSchemaBase]);

  const validSteps = React.useMemo(
    () => steps.filter((s) => stepValidity[s.id]),
    [stepValidity, steps]
  );

  const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
  const isFirst = currentStepIndex <= 0;
  const isLast = currentStepIndex === steps.length - 1;
  const nextStepId =
    currentStepIndex >= 0 && currentStepIndex < steps.length - 1
      ? steps[currentStepIndex + 1].id
      : currentStep;
  const prevStepId = currentStepIndex > 0 ? steps[currentStepIndex - 1].id : currentStep;
  const canProceed = stepValidity[currentStep] ?? true;

  const onNext = async (current) => {
    const fields = STEP_FIELDS[current];
    if (!fields?.length) return true;
    return methods.trigger(fields, { shouldFocus: true });
  };

  const handleNext = async () => {
    const ok = (await onNext?.(currentStep)) ?? true;
    if (ok) {
      dispatch({ type: "SET_STEP", value: nextStepId });
    }
  };

  const saveQuote = React.useCallback(
    async (data) => {
      const carriers = Array.isArray(data?.step1?.carrier)
        ? data.step1.carrier.filter(Boolean)
        : data?.step1?.carrier
        ? [data.step1.carrier]
        : [];

      if (!carriers.length) {
        context.notification.show(
          "Please select at least one carrier before saving.",
          "error",
          true
        );
        return;
      }

      setSavingState(true);
      try {
        const policyType = data?.step1?.quoteType ?? "personal_auto";
        const isRenters = policyType === "renters";
        const numberOfDrivers = isRenters
          ? 0
          : data?.step4?.drivers?.length || 0;
        const numberOfVehicles = isRenters
          ? 0
          : data?.step3?.vehicles?.length || 0;

        const payload = {
          carrier: carriers,
          company_id: location?.state?.data?.company_id ?? null,
          agent_email: data?.step1?.agentEmail ?? "",
          uploaded_by: user?.data?.name ?? "",
          effective_date: data?.step1?.policyDate ?? new Date(),
          policy_type: policyType,
          number_of_drivers: numberOfDrivers,
          number_of_vehicles: numberOfVehicles,
          number_of_trailers: 0,
          premium: 0,
          quote: sanitize(data),
        };

        if (location?.state?.data?.quote_id?.length) {
          await axios.patch(`/api/quote/${location.state.data.quote_id}`, payload);
        } else {
          const res = await axios.post("/api/quote", payload);
          const newId = res?.data?.data?.id;
          if (newId) {
            navigate(location.pathname, {
              state: {
                ...(location.state || {}),
                data: { ...(location.state?.data || {}), quote_id: newId },
              },
              replace: true,
            });
          }
        }

        context.notification.show("Quote saved", "success", true);
      } catch (error) {
        context.notification.show(
          error?.response?.data?.message || "Failed to save quote",
          "error",
          true
        );
      } finally {
        setSavingState(false);
      }
    },
    [context.notification, location, navigate, user?.data?.name]
  );

  const submitQuote = React.useCallback(
    async (data) => {
      const carriers = Array.isArray(data?.step1?.carrier)
        ? data.step1.carrier.filter(Boolean)
        : data?.step1?.carrier
        ? [data.step1.carrier]
        : [];

      if (!carriers.length) {
        context.notification.show("Please select at least one carrier.", "error", true);
        return;
      }

      const agentEmail = data?.step1?.agentEmail;
      if (!agentEmail) {
        context.notification.show("Agent email is required before submission.", "error", true);
        return;
      }

      setSubmitted(false);
      setBannerText("");
      setSubmittingState(true);
      try {
        const policyType = data?.step1?.quoteType ?? "personal_auto";
        const isRenters = policyType === "renters";
        const numberOfDrivers = isRenters
          ? 0
          : data?.step4?.drivers?.length || 0;
        const numberOfVehicles = isRenters
          ? 0
          : data?.step3?.vehicles?.length || 0;

        const payload = {
          carrier: carriers,
          company_id: location?.state?.data?.company_id ?? null,
          agent_email: agentEmail,
          uploaded_by: user?.data?.name ?? "",
          effective_date: data?.step1?.policyDate ?? new Date(),
          policy_type: policyType,
          number_of_drivers: numberOfDrivers,
          number_of_vehicles: numberOfVehicles,
          number_of_trailers: 0,
          premium: 0,
          status: "submitted",
          quote: sanitize(data),
        };

        const submitEndpoint = isRenters
          ? "/api/quote/renters/submit"
          : "/api/quote/personal/submit";

        const res = await axios.post(submitEndpoint, payload);
        const quoteData = res?.data?.data;

        const carrierList = formatCarrierList(carriers);
        const nextBannerText = `This quote will be submitted to: ${carrierList} and you will receive correspondence by email to ${agentEmail}.`;
        setBannerText(nextBannerText);
        setSubmitted(true);
        context.notification.show("Quote submitted", "success", true);
        navigate("/app/quote/review", {
          state: {
            data: quoteData,
            bannerText: nextBannerText,
          },
        });
      } catch (error) {
        context.notification.show(
          error?.response?.data?.message || "Failed to submit quote",
          "error",
          true
        );
      } finally {
        setSubmittingState(false);
      }
    },
    [context.notification, location?.state?.data?.company_id, user?.data?.name]
  );

  const handleFinalSubmit = React.useCallback(() => {
    handleSubmit((data) => submitQuote?.(data))();
  }, [handleSubmit, submitQuote]);

  const handleSave = React.useCallback(() => {
    const latestValues = methods.getValues();
    saveQuote?.(latestValues);
  }, [methods, saveQuote]);

  const producerOptions = React.useMemo(() => {
    if (producers.loading || !producers.data?.users) return [];
    return producers.data.users.map((u) => ({
      value: String(u.id),
      label: u.name,
      email: u.email,
    }));
  }, [producers.loading, producers.data]);

  const resolvedStepComponents = React.useMemo(() => {
    if (typeof stepComponents === "function") {
      return (
        stepComponents({
          user,
          producers,
          methods,
          submitted,
          bannerText,
          producerOptions,
        }) || {}
      );
    }
    return stepComponents || {};
  }, [
    stepComponents,
    user,
    producers,
    methods,
    submitted,
    bannerText,
    producerOptions,
  ]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  React.useEffect(() => {
    const lastValidStepId = validSteps[validSteps.length - 1]?.id;
    if (lastValidStepId !== undefined && lastValidStepId !== maxStepReached) {
      dispatch({
        type: "SET_MAX_STEP_REACHED",
        value: lastValidStepId,
      });
    }
  }, [dispatch, maxStepReached, validSteps]);

  return (
    <div className="flex flex-col items-center justify-start h-full p-4">
      <div className="flex justify-end w-full max-w-7xl mb-4">
        <Button
          variant="outline"
          onClick={() => dispatch({ type: "TOGGLE_SINGLE_PAGE" })}
        >
          {singlePage ? "See As Stepper" : "See As Single Page"}
        </Button>
      </div>

      {!singlePage && (
        <div className="flex items-center gap-6 mb-6 w-full max-w-7xl flex-wrap justify-center">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = step.id === currentStep;
            const isCompleted = idx < currentStepIndex && stepValidity[step.id];
            const prevStepsValid = steps
              .slice(0, idx)
              .every((s) => stepValidity[s.id]);
            const isClickable =
              isActive || (step.id <= maxStepReached && prevStepsValid);

            return (
              <div
                key={step.id}
                className={cn(
                  "flex items-center gap-2 cursor-default",
                  isClickable && "cursor-pointer"
                )}
                onClick={() =>
                  isClickable && dispatch({ type: "SET_STEP", value: step.id })
                }
              >
                <div
                  className={cn(
                    "flex items-center justify-center w-12 h-12 rounded-full border-2 transition-colors",
                    isActive && "bg-red-600 text-white border-red-600",
                    isCompleted &&
                      "bg-green-600 text-white border-green-600 hover:bg-green-700",
                    !isActive && !isCompleted && "border-gray-300 text-gray-400"
                  )}
                >
                  <Icon className="w-6 h-6" />
                </div>

                {isActive && (
                  <div className="flex flex-col text-left">
                    <span className="text-sm text-gray-500">
                      Step {idx + 1}/{steps.length}
                    </span>
                    <span className="text-base font-medium text-gray-900">
                      {step.label}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Card className="w-full max-w-7xl">
        <CardContent className="p-6 space-y-8">
          {singlePage
            ? steps.map((s) => (
                <div key={s.id} className="space-y-4">
                  {resolvedStepComponents[s.id]}
                </div>
              ))
            : resolvedStepComponents[currentStep]}
        </CardContent>
      </Card>

      <div className="flex justify-between md:justify-end mt-8 w-full max-w-7xl gap-2">
        {!singlePage && (
          <>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={savingState || submittingState}
            >
              {savingState ? "Saving..." : "Save"}
            </Button>

            <Button
              variant="outline"
              onClick={() => dispatch({ type: "SET_STEP", value: prevStepId })}
              className="min-w-[10ch]"
              disabled={isFirst || submittingState || savingState}
            >
              Back
            </Button>

            {!isLast && (
              <Button
                variant="outline"
                className={
                  canProceed
                    ? "bg-green-600 text-white hover:bg-green-700 hover:text-white"
                    : "bg-gray-800 text-white hover:bg-gray-700 hover:text-white"
                }
                onClick={handleNext}
                disabled={submittingState || savingState || !canProceed}
              >
                Next Step
              </Button>
            )}

            {isLast && (
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={handleFinalSubmit}
                disabled={!canSubmit || submittingState || savingState}
              >
                {submittingState ? "Submitting..." : "Submit"}
              </Button>
            )}
          </>
        )}

        {singlePage && (
          <>
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={savingState || submittingState}
            >
              {savingState ? "Saving..." : "Save"}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleFinalSubmit}
              disabled={!canSubmit || submittingState || savingState}
            >
              {submittingState ? "Submitting..." : "Submit"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
